import { useAuth0 } from '@auth0/auth0-react';
import { ChevronDown, ChevronUp, MessageSquarePlus } from 'lucide-react';
import React, { useState } from 'react';
import {
  Button,
  Card,
  Collapse,
  Form,
  Toast,
  ToastContainer,
} from 'react-bootstrap';
import TurndownService from 'turndown';
import MediaListBlock from '../blocks/MediaListBlock';
import './UserFeedbackCard.css';

import { marked } from 'marked';
// Initialize turndown service for HTML to markdown conversion
const turndownService = new TurndownService({
  headingStyle: 'atx',
  bulletListMarker: '-',
  codeBlockStyle: 'fenced',
});

// Function to convert HTML content to markdown
const convertHtmlToMarkdown = (htmlContent: string): string => {
  if (!htmlContent) return '';

  try {
    // Convert HTML to markdown using turndown
    return turndownService.turndown(htmlContent);
  } catch (error) {
    console.warn('Failed to convert HTML to markdown:', error);
    // Fallback to original content if conversion fails
    return htmlContent;
  }
};

// Function to convert plain text to markdown format
const convertTextToMarkdown = (text: string): string => {
  if (!text) return '';

  // Convert plain text to markdown, handling line breaks properly
  return text
    .trim()
    .split('\n')
    .map(line => line.trim())
    .join('\n')
    .replace(/\n\n+/g, '\n\n'); // Normalize multiple line breaks to double line breaks
};

interface CardData {
  collapse_settings?: {
    open?: boolean;
    title?: string;
  };
  title?: string;
  blocks?: any[];
}

interface UserFeedbackCardProps {
  card: CardData;
}

const UserFeedbackCard: React.FC<UserFeedbackCardProps> = ({ card }) => {
  const { getIdTokenClaims } = useAuth0();
  const [isOpen, setIsOpen] = useState<boolean>(
    card?.collapse_settings?.open ?? true
  );
  const [showForm, setShowForm] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>('');
  const [toastType, setToastType] = useState<string>('success');
  const [feedbackData, setFeedbackData] = useState<any>(null);
  const [isLoadingFeedback, setIsLoadingFeedback] = useState<boolean>(true);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  // Function to extract response_id from the API body
  const getResponseId = (): string | null => {
    const submitAction = card.blocks?.find(
      block => block.kind === 'button_list'
    )?.buttons?.[0]?.modal_display?.content?.submit_action;
    if (submitAction?.body) {
      const match = submitAction.body.match(/"response_id":\s*"([^"]+)"/);
      return match ? match[1] : null;
    }
    return null;
  };

  // Function to fetch feedback data from API
  const fetchFeedbackData = async () => {
    try {
      const responseId = getResponseId();

      if (!responseId) {
        console.error('No response_id found in card data');
        setIsLoadingFeedback(false);
        return;
      }

      const idToken = await getIdTokenClaims();
      const idTokenString = idToken.__raw;

      const response = await fetch(
        `https://api.dev-ept-ai.com/v3/response/ranking?response_id=${responseId}`,
        {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${idTokenString}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setFeedbackData(data);
      } else {
        console.error('Failed to fetch feedback data:', response.status);
      }
    } catch (error) {
      console.error('Error fetching feedback data:', error);
    } finally {
      setIsLoadingFeedback(false);
    }
  };

  // Transform response data to MediaListBlock format
  const transformResponseDataToMediaList = (responseData: any) => {
    // Handle both old format (comments array) and new format (ratings.text)
    if (responseData.comments && responseData.comments.length > 0) {
      // Old format with comments array
      const items = responseData.comments.map(comment => {
        const convertedComment = marked(comment.comment);

        return {
          icon: {
            type: 'url',
            value: comment.picture,
            size: 'medium',
            crop: 'circle',
            color: null,
            action: null,
          },
          title: comment.name,
          description: `> ${convertedComment}\n\n*${comment.uesr_id} • ${comment.date}*`,
          clickable: false,
          details: null,
        };
      });

      return {
        kind: 'media_list',
        items: items,
      };
    } else if (responseData.ratings && responseData.ratings.text) {
      // New format with ratings.text - create a single comment item
      const textContent = marked(responseData.ratings.text);

      return {
        kind: 'media_list',
        items: [
          {
            icon: {
              type: 'lucide',
              value: 'message-square',
              size: 'medium',
              crop: 'circle',
              color: null,
              action: null,
            },
            title: 'Recent Feedback',
            description: `> ${textContent}`,
            clickable: false,
            details: null,
          },
        ],
      };
    }

    return null;
  };

  // Load feedback data on component mount
  React.useEffect(() => {
    fetchFeedbackData();
  }, []);

  // Track feedbackData state changes
  React.useEffect(() => {
    // feedbackData updated
  }, [feedbackData]);

  const handleToggleForm = () => {
    setShowForm(!showForm);
    if (showForm) {
      setFeedback(''); // Clear form when hiding
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setFeedback('');
  };

  const handleSubmitFeedback = async () => {
    if (!feedback.trim()) {
      setToastMessage('Please enter feedback before submitting');
      setToastType('error');
      setShowToast(true);
      return;
    }

    setIsSubmitting(true);

    try {
      // Get the submit action from the card data
      const buttonBlock = card.blocks?.find(
        block => block.kind === 'button_list'
      );
      const submitAction =
        buttonBlock?.buttons?.[0]?.modal_display?.content?.submit_action;

      const idToken = await getIdTokenClaims();
      const idTokenString = idToken.__raw;
      if (submitAction) {
        // Convert user feedback text to markdown format
        const markdownFeedback = convertTextToMarkdown(feedback);

        // Properly escape the feedback for JSON replacement
        const escapedFeedback = JSON.stringify(markdownFeedback).slice(1, -1); // Remove surrounding quotes

        // Replace {feedback} placeholder in the body with properly escaped markdown text
        const body = submitAction.body.replace('{feedback}', escapedFeedback);

        const response = await fetch(submitAction.url, {
          method: submitAction.method,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${idTokenString}`,
            ...JSON.parse(submitAction.headers || '{}'),
          },
          body: body,
        });

        const statusHandlers = submitAction.response?.status_handlers || [];
        const handler =
          statusHandlers.find(h => h.status_code === response.status) ||
          submitAction.response?.default_handler;

        if (handler) {
          setToastMessage(handler.message);
          setToastType(handler.toast_type || 'success');
          setShowToast(true);

          if (handler.show_toast && response.ok) {
            setFeedback('');
            setShowForm(false);

            // Use the response data directly instead of making another API call
            const responseData = await response.json();

            if (responseData) {
              setFeedbackData(responseData);
            }
          }
        } else {
          throw new Error('No status handler found');
        }
      } else {
        setToastMessage('Configuration error: No submit action found');
        setToastType('error');
        setShowToast(true);
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setToastMessage('Failed to submit feedback. Please try again.');
      setToastType('error');
      setShowToast(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderHeader = () => {
    if (!card.title) return null;

    if (card.collapse_settings) {
      return (
        <Card.Header
          as="h5"
          onClick={handleToggle}
          className="collapsible-header"
          style={{
            cursor: 'pointer',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <span>{card.collapse_settings.title || card.title}</span>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </Card.Header>
      );
    }

    return <Card.Header as="h5">{card.title}</Card.Header>;
  };

  const textBlock = card.blocks?.find(block => block.kind === 'text');
  const originalMediaListBlock = card.blocks?.find(
    block => block.kind === 'media_list'
  );
  const buttonBlock = card.blocks?.find(block => block.kind === 'button_list');
  const addButton = buttonBlock?.buttons?.[0];

  // Simple approach: Use original data first, then API data when available
  let mediaListBlock = originalMediaListBlock;
  let hasExistingFeedback = originalMediaListBlock?.items?.length > 0;

  // Only use API data if we have actual data, otherwise keep original data
  if (feedbackData) {
    const transformedBlock = transformResponseDataToMediaList(feedbackData);
    if (transformedBlock) {
      mediaListBlock = transformedBlock;
      hasExistingFeedback = true;
    }
  }

  return (
    <>
      <Card className="mb-4 user-feedback-card">
        {renderHeader()}
        <Collapse in={!card.collapse_settings || isOpen}>
          <div>
            <Card.Body>
              {/* Loading state */}
              {isLoadingFeedback && (
                <div className="text-center py-3">
                  <div
                    className="spinner-border spinner-border-sm"
                    role="status"
                  >
                    <span className="visually-hidden">Loading feedback...</span>
                  </div>
                  <span className="ms-2 text-muted">Loading feedback...</span>
                </div>
              )}

              {/* Show existing feedback if available */}
              {hasExistingFeedback && mediaListBlock && (
                <div className="existing-feedback mb-3">
                  <MediaListBlock
                    key={
                      feedbackData
                        ? `api-${feedbackData.comments?.length || 0}`
                        : 'original'
                    }
                    block={mediaListBlock}
                  />
                </div>
              )}

              {/* Show no feedback message only if no existing feedback and form is not shown */}
              {!isLoadingFeedback &&
                !hasExistingFeedback &&
                textBlock &&
                !showForm && (
                  <p className="feedback-text">{textBlock.content}</p>
                )}

              {!showForm && addButton && (
                <div
                  className={
                    buttonBlock.layout === 'right'
                      ? 'btn-group-right'
                      : 'btn-group-left'
                  }
                >
                  <Button
                    variant={addButton.variant || 'primary'}
                    onClick={handleToggleForm}
                    className="button-with-icon"
                    disabled={isLoadingFeedback}
                  >
                    {addButton.icon?.type === 'lucide' &&
                      addButton.icon?.value === 'message-square-plus' && (
                        <MessageSquarePlus size={16} />
                      )}
                    {addButton.label}
                  </Button>
                </div>
              )}

              {showForm && (
                <div className="feedback-form">
                  <Form>
                    {addButton?.modal_display?.content?.fields?.map(
                      (field, index) => (
                        <Form.Group key={index} className="mb-3">
                          <Form.Label>
                            {field.label}
                            {field.required && (
                              <span className="required-asterisk">*</span>
                            )}
                          </Form.Label>
                          {field.type === 'long_text' ? (
                            <Form.Control
                              as="textarea"
                              rows={4}
                              placeholder={field.placeholder}
                              value={feedback}
                              onChange={e => setFeedback(e.target.value)}
                              required={field.required}
                            />
                          ) : (
                            <Form.Control
                              type="text"
                              placeholder={field.placeholder}
                              value={feedback}
                              onChange={e => setFeedback(e.target.value)}
                              required={field.required}
                            />
                          )}
                        </Form.Group>
                      )
                    )}
                  </Form>

                  <div className="form-actions d-flex gap-2 justify-content-end">
                    <Button variant="secondary" onClick={handleCancelForm}>
                      Cancel
                    </Button>
                    <Button
                      variant="primary"
                      onClick={handleSubmitFeedback}
                      disabled={isSubmitting || !feedback.trim()}
                      className="btn-submit"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Feedback'}
                    </Button>
                  </div>
                </div>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* Toast Notifications */}
      <ToastContainer
        position="top-end"
        className="position-fixed p-3"
        style={{ zIndex: 9999 }}
      >
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg={toastType === 'success' ? 'success' : 'danger'}
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default UserFeedbackCard;
